import {useRef} from 'react';

import {useUpdateFeatureMutation, type FeatureResponseBody} from '@dms/api/new-vehicle';

import {featureTaxonomyUpdates} from '../utils/featureTaxonomyUpdates';

type TaxonomyValues = {memberFeatureIds: string[]; packageOnlyFeatureIds: string[]};

/**
 * The library taxonomy the package builder edits in place: whether each member is orderable alone.
 * Each saved feature is checkpointed, so a retry after a failed save sends only the members that
 * still differ, with the version their last save left.
 */
export function usePackageTaxonomySave(features: FeatureResponseBody[]) {
  const [updateFeature] = useUpdateFeatureMutation();
  const featureCheckpoints = useRef(
    new Map<string, {standaloneOrderable: boolean; version: number}>()
  );
  /** The feature writes of the save: one per member whose "orderable alone" changes. */
  const taxonomyUpdates = (values: TaxonomyValues) =>
    featureTaxonomyUpdates(features, values, (featureId) =>
      featureCheckpoints.current.get(featureId)
    );
  const saveTaxonomy = async (values: TaxonomyValues) => {
    for (const update of taxonomyUpdates(values)) {
      const savedFeature = await updateFeature(update).unwrap();
      featureCheckpoints.current.set(update.featureId, {
        standaloneOrderable: savedFeature.standaloneOrderable,
        version: savedFeature.version,
      });
    }
  };
  return {saveTaxonomy, taxonomyUpdates};
}
